// Ursa Major III (UMa3 / UNIONS 1) — the faintest known Milky Way satellite, and the setting of the
// campaign. Every physical number here is REAL (Smith et al. 2024, ApJ + the object's Wikipedia
// infobox); we keep the science accurate and only imagine the planets. It's an ancient (~11 Gyr),
// metal-poor, barely-bound scatter of ~57 cool low-mass stars ~32,600 ly out in the Galactic halo —
// so the cluster reads as faint AMBER/ORANGE stars in a cold void (perfect for the qualionix palette).

import * as THREE from "three";

export const UMA3 = {
  name: "URSA MAJOR III",
  alt: "UNIONS 1",
  desig: "UMa3 / U1",
  distanceLy: 32600, // 32.6 ± 3.3 kly (10 ± 1 kpc)
  halfLightPc: 4.8,
  diameterLy: 19.6,
  starCount: 57, // 57 (+21 / -19)
  absMagV: 2.2,
  luminosityLsun: 11.4,
  ageGyr: 11,
  metallicity: "[Fe/H] ≈ -2",
  ra: "11h38m49.8s",
  dec: "+31°04'42\"",
  type: "dSph? / cluster?", // its very nature is unresolved — a campaign hook
} as const;

export interface ClusterStar {
  pos: THREE.Vector3; // light-years, cluster-centered
  color: THREE.Color;
  tempK: number;
  size: number; // render size
  lum: number; // relative brightness (drives bloom)
  giant: boolean;
}

export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Old, metal-poor stellar temperatures → warm RGB (deep red 3200K → pale yellow-white 6200K). */
export function tempColor(k: number): THREE.Color {
  const t = Math.max(0, Math.min(1, (k - 3200) / 3000));
  return new THREE.Color(1.0, 0.42 + t * 0.52, 0.16 + t * t * 0.64);
}

/** Rough spectral class label from temperature (for HUD flavor). */
export function spectralClass(k: number): string {
  if (k >= 6000) return "F8";
  if (k >= 5200) return "G6";
  if (k >= 4400) return "K3";
  if (k >= 3800) return "K7";
  return "M2";
}

/** Generate the real ~57-star population: an elongated, sparse, ancient scatter — a few brighter red
 *  giants dominating the light, the rest faint cool low-mass stars. Deterministic. Positions in ly. */
export function generateCluster(seed = 1138): ClusterStar[] {
  const rng = mulberry32(seed);
  const stars: ClusterStar[] = [];
  const rScale = 6.5; // Plummer scale radius (ly)
  const axis = new THREE.Vector3(1.0, 0.5, 0.62); // elongated + slightly flattened (tidal hint)
  for (let i = 0; i < UMA3.starCount; i++) {
    // Plummer inverse-CDF radius, clamped so no star runs to infinity
    const m = Math.min(0.985, rng());
    const r = Math.min(24, rScale / Math.sqrt(Math.pow(m, -2 / 3) - 1));
    const th = Math.acos(2 * rng() - 1);
    const ph = rng() * Math.PI * 2;
    const dir = new THREE.Vector3(
      Math.sin(th) * Math.cos(ph),
      Math.cos(th),
      Math.sin(th) * Math.sin(ph),
    ).multiply(axis);
    const pos = dir.multiplyScalar(r);
    const giant = i < 3; // ~3 bright old red giants carry most of the 11.4 L☉
    const tempK = giant ? 3900 + rng() * 500 : 3400 + rng() * 1900;
    const lum = giant ? 1.7 + rng() * 1.3 : 0.14 + rng() * 0.5;
    const size = giant ? 2.6 + rng() * 0.9 : 0.7 + rng() * 0.9;
    stars.push({ pos, color: tempColor(tempK), tempK, size, lum, giant });
  }
  return stars;
}
